import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Container, Spinner } from "react-bootstrap";
import { Col, Form, Input, Row } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheck } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { TailSpin } from "react-loader-spinner";
import { useToasts } from "../contexts/toasts";
import { Users } from "../api/Users";
import Logo from "../common/Logo";
import logo from "../assets/img/LOGO CLAUDIO MESSINA_MONOGRAMMA PNG_MONOGRAMMA COLORE-05.png";

export default function VerifyUser() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    Users.getUserByToken(token).then(
      () => setTokenValid(true),
      (err) => {
        setTokenValid(false);
        const msg =
          err?.response?.data?.message ||
          "C'è stato un errore, riprova o contatta il trainer";
        setErrorMessage(msg);
      }
    );
  }, [token, navigate]);

  const handleSubmit = useCallback(
    (values: any) => {
      if (!token) return;
      setLoading(true);
      Users.verifyUser({
        token,
        oldPassword: values.oldPassword,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      }).then(
        () => {
          setLoading(false);
          showToast(
            "verify-success",
            "Congratulazioni! Hai confermato il tuo account. Adesso puoi effettuare il login",
            "",
            "success"
          );
          navigate("/login");
        },
        (err) => {
          setLoading(false);
          const msg =
            err?.response?.data?.message ||
            "Errore nella verifica dell'utente";
          showToast("verify-error", msg, "", "danger");
        }
      );
    },
    [token, navigate, showToast]
  );

  if (tokenValid === null) {
    return (
      <Container className="login-container">
        <div className="d-flex justify-content-center py-5">
          <TailSpin height={80} width={80} color="#f25d0b" radius="1" />
        </div>
      </Container>
    );
  }

  if (tokenValid === false) {
    return (
      <Container className="login-container">
        <Card>
          <Card.Body className="text-center py-5">
            <Logo logo={logo} width={200} className="my-4" />
            <h5>Ops!</h5>
            <p>{errorMessage}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="login-container">
      <Card>
        <Card.Body>
          <Form
            className="align-items-center"
            form={form}
            onFinish={handleSubmit}
          >
            <div className="text-center mb-5">
              <Logo logo={logo} width={200} className="my-4" />
              <Card.Subtitle className="text-muted">
                Attiva il tuo Account Coaching Master
              </Card.Subtitle>
            </div>
            <Form.Item
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
              wrapperCol={{
                xs: { span: 24 },
                md: { span: 8, offset: 8 },
              }}
              name="oldPassword"
              label="Password Temporanea"
              rules={[
                {
                  required: true,
                  message: "La password temporanea è obbligatoria",
                },
              ]}
            >
              <Input size="large" placeholder="Password temporanea..." />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
              wrapperCol={{
                xs: { span: 24 },
                md: { span: 8, offset: 8 },
              }}
              name="password"
              label="Nuova Password"
              rules={[
                {
                  required: true,
                  message: "La nuova password è obbligatoria",
                },
                {
                  min: 6,
                  message: "La password deve contenere almeno 6 caratteri",
                },
              ]}
            >
              <Input.Password
                placeholder="Nuova password..."
                size="large"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
              wrapperCol={{
                xs: { span: 24 },
                md: { span: 8, offset: 8 },
              }}
              name="passwordConfirmation"
              label="Conferma Password"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: "Devi confermare la nuova password",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Le password non coincidono")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Conferma password..."
                size="large"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>
            <Row className="justify-content-center">
              <Col sm={10} md={5}>
                <button
                  type="submit"
                  disabled={loading}
                  className={classNames("w-100 button-primary", {
                    "button-disabled": loading,
                  })}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      <span className="ms-2">Conferma</span>
                    </>
                  )}
                </button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
